import { useEffect } from 'react';
import { Navigate, isRouteErrorResponse, useRouteError } from 'react-router';

import { captureUnexpectedError } from '@/lib/sentry';
import { ServiceError, ServiceErrorCode } from '@/services/utils/service-error';

import { ErrorScreen } from '@/components/error-screen';

const AUTH_ERROR_CODES = new Set<string>([
  ServiceErrorCode.InvalidCredentials,
  ServiceErrorCode.InvalidToken,
  ServiceErrorCode.UserOrganizationCtxNotFound,
  ServiceErrorCode.UserOrganizationNotFound,
  ServiceErrorCode.CsrfTokenMismatch,
  ServiceErrorCode.ForbiddenResource,
]);

function isAuthError(error: unknown): boolean {
  return error instanceof ServiceError && AUTH_ERROR_CODES.has(error.errorCode);
}

export function RouteErrorBoundary() {
  const error = useRouteError();

  // notFound() throws a raw Response from render; isRouteErrorResponse only
  // matches errors thrown from loaders/actions, so check both.
  const statusError =
    isRouteErrorResponse(error) || error instanceof Response ? error : null;

  useEffect(() => {
    // Intentional Responses (404s) aren't reported; ServiceErrors already
    // were, by the query/mutation caches.
    if (statusError || isAuthError(error) || error instanceof ServiceError) {
      return;
    }
    captureUnexpectedError(error);
  }, [error, statusError]);

  if (isAuthError(error)) {
    return <Navigate to="/login" replace />;
  }

  if (statusError) {
    return <ErrorScreen status={statusError.status} />;
  }

  return <ErrorScreen status={500} />;
}
